import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import {
  getAllGoggleModels,
  getGoggleModelById,
  createGoggleModel,
  updateGoggleModel,
  deleteGoggleModel,
  updateModelGlassOptions,
  updateModelBandOptions,
  updateModelProductTypes,
  getAllGlassOptions,
  getAllBandOptions,
  GoggleModel,
  GlassOption,
  BandOption,
} from '../services/admin';
import './AdminModelsPage.css';

const AdminModelsPage: React.FC = () => {
  const [models, setModels] = useState<GoggleModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<GoggleModel | null>(null);
  const [allGlassOptions, setAllGlassOptions] = useState<GlassOption[]>([]);
  const [allBandOptions, setAllBandOptions] = useState<BandOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Model editing
  const [editingModel, setEditingModel] = useState<GoggleModel | null>(null);
  const [newModelName, setNewModelName] = useState('');
  const [newModelDescription, setNewModelDescription] = useState('');
  const [newModelActive, setNewModelActive] = useState(true);

  // Option assignments
  const [selectedGlassOptions, setSelectedGlassOptions] = useState<Set<string>>(new Set());
  const [selectedBandOptions, setSelectedBandOptions] = useState<Set<string>>(new Set());
  const [selectedProductTypes, setSelectedProductTypes] = useState<Set<'CLIP' | 'COMPLETE_GLASSES'>>(
    new Set()
  );

  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedModel) {
      // Load full model details
      loadModelDetails(selectedModel.id);
    }
  }, [selectedModel?.id]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [modelsData, glassData, bandData] = await Promise.all([
        getAllGoggleModels(),
        getAllGlassOptions(),
        getAllBandOptions(),
      ]);
      setModels(modelsData);
      setAllGlassOptions(glassData);
      setAllBandOptions(bandData);
    } catch (error: any) {
      console.error('Error loading data:', error);
      setError(error.response?.data?.error || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const loadModelDetails = async (modelId: string) => {
    try {
      const model = await getGoggleModelById(modelId);
      setSelectedModel(model);

      // Set selected options
      const glassIds = new Set(
        (model.glassOptions || []).filter(go => go.active).map(go => go.glassOption.id)
      );
      const bandIds = new Set(
        (model.bandOptions || []).filter(bo => bo.active).map(bo => bo.bandOption.id)
      );
      const productTypes = new Set(
        (model.productTypes || []).filter(pt => pt.active).map(pt => pt.productType)
      ) as Set<'CLIP' | 'COMPLETE_GLASSES'>;

      setSelectedGlassOptions(glassIds);
      setSelectedBandOptions(bandIds);
      setSelectedProductTypes(productTypes);
    } catch (error: any) {
      console.error('Error loading model details:', error);
      setError(error.response?.data?.error || 'Fehler beim Laden der Modelldetails');
    }
  };

  const handleModelClick = (model: GoggleModel) => {
    setSelectedModel(model);
    setEditingModel(null);
  };

  const handleCloseDetails = () => {
    setSelectedModel(null);
    setEditingModel(null);
    setSelectedGlassOptions(new Set());
    setSelectedBandOptions(new Set());
    setSelectedProductTypes(new Set());
  };

  const handleCreateModel = async () => {
    if (!newModelName.trim()) {
      setError('Name ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const model = await createGoggleModel({
        name: newModelName.trim(),
        description: newModelDescription.trim() || undefined,
        active: newModelActive,
      });
      setModels([...models, model]);
      setNewModelName('');
      setNewModelDescription('');
      setNewModelActive(true);
      // Automatically select the newly created model so user can assign options
      // Load full model details to ensure we have the proper structure
      await loadModelDetails(model.id);
      setSuccess('Modell erfolgreich erstellt');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Fehler beim Erstellen des Modells');
    } finally {
      setSaving(false);
    }
  };

  const handleEditModel = (model: GoggleModel) => {
    setEditingModel(model);
    setNewModelName(model.name);
    setNewModelDescription(model.description || '');
    setNewModelActive(model.active);
  };

  const handleUpdateModel = async () => {
    if (!editingModel || !newModelName.trim()) {
      setError('Name ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const updated = await updateGoggleModel(editingModel.id, {
        name: newModelName.trim(),
        description: newModelDescription.trim() || undefined,
        active: newModelActive,
      });
      setModels(models.map(m => (m.id === updated.id ? updated : m)));
      if (selectedModel?.id === updated.id) {
        setSelectedModel(updated);
      }
      setEditingModel(null);
      setSuccess('Modell erfolgreich aktualisiert');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Fehler beim Aktualisieren des Modells');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModel = async (modelId: string) => {
    if (!window.confirm('Möchtest du dieses Modell wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await deleteGoggleModel(modelId);
      setModels(models.filter(m => m.id !== modelId));
      if (selectedModel?.id === modelId) {
        handleCloseDetails();
      }
      setSuccess('Modell erfolgreich gelöscht');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Fehler beim Löschen des Modells');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGlassOptions = async () => {
    if (!selectedModel) return;

    setSaving(true);
    setError(null);
    try {
      await updateModelGlassOptions(selectedModel.id, Array.from(selectedGlassOptions));
      await loadModelDetails(selectedModel.id);
      await loadData(); // Reload all models
      setSuccess('Glasoptionen erfolgreich aktualisiert');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Fehler beim Aktualisieren der Glasoptionen');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBandOptions = async () => {
    if (!selectedModel) return;

    setSaving(true);
    setError(null);
    try {
      await updateModelBandOptions(selectedModel.id, Array.from(selectedBandOptions));
      await loadModelDetails(selectedModel.id);
      await loadData(); // Reload all models
      setSuccess('Bandoptionen erfolgreich aktualisiert');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Fehler beim Aktualisieren der Bandoptionen');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProductTypes = async () => {
    if (!selectedModel) return;

    if (selectedProductTypes.size === 0) {
      setError('Mindestens ein Produkttyp muss ausgewählt sein');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateModelProductTypes(selectedModel.id, Array.from(selectedProductTypes));
      await loadModelDetails(selectedModel.id);
      await loadData(); // Reload all models
      setSuccess('Produkttypen erfolgreich aktualisiert');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Fehler beim Aktualisieren der Produkttypen');
    } finally {
      setSaving(false);
    }
  };

  const toggleGlassOption = (optionId: string) => {
    const newSet = new Set(selectedGlassOptions);
    if (newSet.has(optionId)) {
      newSet.delete(optionId);
    } else {
      newSet.add(optionId);
    }
    setSelectedGlassOptions(newSet);
  };

  const toggleBandOption = (optionId: string) => {
    const newSet = new Set(selectedBandOptions);
    if (newSet.has(optionId)) {
      newSet.delete(optionId);
    } else {
      newSet.add(optionId);
    }
    setSelectedBandOptions(newSet);
  };

  const toggleProductType = (type: 'CLIP' | 'COMPLETE_GLASSES') => {
    const newSet = new Set(selectedProductTypes);
    if (newSet.has(type)) {
      newSet.delete(type);
    } else {
      newSet.add(type);
    }
    setSelectedProductTypes(newSet);
  };

  return (
    <div>
      <AdminHeader />
      <div className="container" style={{ padding: '2rem 1rem' }}>
        <h1>Brillenmodelle verwalten</h1>

        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        {loading ? (
          <div>Lade Daten...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem' }}>
            {/* Left Column: Model List */}
            <div>
              <div className="card">
                <h2>Modelle</h2>
                <div style={{ marginBottom: '1rem' }}>
                  <h3>Neues Modell erstellen</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <input
                      type="text"
                      placeholder="Modellname"
                      value={newModelName}
                      onChange={e => setNewModelName(e.target.value)}
                      disabled={saving || !!editingModel}
                    />
                    <textarea
                      placeholder="Beschreibung (optional)"
                      value={newModelDescription}
                      onChange={e => setNewModelDescription(e.target.value)}
                      disabled={saving || !!editingModel}
                      rows={2}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={newModelActive}
                        onChange={e => setNewModelActive(e.target.checked)}
                        disabled={saving || !!editingModel}
                      />
                      Aktiv
                    </label>
                    {editingModel ? (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button onClick={handleUpdateModel} disabled={saving}>
                          Aktualisieren
                        </button>
                        <button onClick={() => { setEditingModel(null); setNewModelName(''); setNewModelDescription(''); setNewModelActive(true); }} disabled={saving}>
                          Abbrechen
                        </button>
                      </div>
                    ) : (
                      <button onClick={handleCreateModel} disabled={saving}>
                        Erstellen
                      </button>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: '2rem' }}>
                  <h3>Vorhandene Modelle</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {models.map(model => (
                      <div
                        key={model.id}
                        style={{
                          padding: '1rem',
                          border: selectedModel?.id === model.id ? '2px solid var(--accent-color3)' : '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          backgroundColor: selectedModel?.id === model.id ? '#f0f0f0' : 'white',
                        }}
                        onClick={() => handleModelClick(model)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <strong>{model.name}</strong>
                            {!model.active && <span style={{ color: '#999', marginLeft: '0.5rem' }}>(inaktiv)</span>}
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                handleEditModel(model);
                              }}
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                            >
                              Bearbeiten
                            </button>
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                handleDeleteModel(model.id);
                              }}
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem', backgroundColor: '#dc3545' }}
                            >
                              Löschen
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Model Details */}
            {selectedModel && (
              <div>
                <div className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h2>{selectedModel.name}</h2>
                    <button onClick={handleCloseDetails}>Schließen</button>
                  </div>

                  {/* Glass Options */}
                  <div style={{ marginBottom: '2rem' }}>
                    <h3>Glasoptionen</h3>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', padding: '1rem', borderRadius: '4px' }}>
                      {allGlassOptions.map(option => (
                        <label key={option.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <input
                            type="checkbox"
                            checked={selectedGlassOptions.has(option.id)}
                            onChange={() => toggleGlassOption(option.id)}
                            disabled={saving || !option.active}
                          />
                          <span style={{ opacity: option.active ? 1 : 0.5 }}>
                            {option.name} {!option.active && '(inaktiv)'}
                          </span>
                        </label>
                      ))}
                    </div>
                    <button onClick={handleSaveGlassOptions} disabled={saving} style={{ marginTop: '0.5rem' }}>
                      Glasoptionen speichern
                    </button>
                  </div>

                  {/* Band Options */}
                  <div style={{ marginBottom: '2rem' }}>
                    <h3>Bandoptionen</h3>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', padding: '1rem', borderRadius: '4px' }}>
                      {allBandOptions.map(option => (
                        <label key={option.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <input
                            type="checkbox"
                            checked={selectedBandOptions.has(option.id)}
                            onChange={() => toggleBandOption(option.id)}
                            disabled={saving || !option.active}
                          />
                          <span style={{ opacity: option.active ? 1 : 0.5 }}>
                            {option.name} {!option.active && '(inaktiv)'}
                          </span>
                        </label>
                      ))}
                    </div>
                    <button onClick={handleSaveBandOptions} disabled={saving} style={{ marginTop: '0.5rem' }}>
                      Bandoptionen speichern
                    </button>
                  </div>

                  {/* Product Types */}
                  <div>
                    <h3>Produkttypen</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={selectedProductTypes.has('CLIP')}
                          onChange={() => toggleProductType('CLIP')}
                          disabled={saving}
                        />
                        <span>Clip</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={selectedProductTypes.has('COMPLETE_GLASSES')}
                          onChange={() => toggleProductType('COMPLETE_GLASSES')}
                          disabled={saving}
                        />
                        <span>Komplette Brille</span>
                      </label>
                    </div>
                    <button onClick={handleSaveProductTypes} disabled={saving}>
                      Produkttypen speichern
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminModelsPage;

